import React, {
  useState,
  Dispatch,
  Reducer,
  useEffect,
  useRef,
  useCallback,
} from "react";
import produce from "immer";
import Form from "@amzn/awsui-components-react-v3/polaris/form";
import WizardStepContextProvider from "./WizardStepContextProvider";
import { WizardStepProps } from "../WizardStep/WizardStep";
import WizardResultContextProvider from "./WizardResultContextProvider";
import WizardWorkflowContextProvider from "./WizardWorkflowContextProvider";
import { StringObjectType } from "../StringObjectType";
import WizardNavigation from "../../workflow/WizardNavigation";
import DefaultWizardCancelModal from "../WizardCancelModal/DefaultWizardCancelModal";
import wizardStyles from "./wizard.module.css";
import workflowStyles from "../../workflow/workflow.module.css";

interface WizardProps<T extends StringObjectType<T>, TContext = undefined> {
  result: T;
  updateResult: Dispatch<(input: T) => void>;
  steps: Extract<keyof T, string>[];
  initialStepNumber?: number;
  workflowContext?: TContext;
  onConfirmCancel?: () => void;
  children:
    | React.ReactElement<WizardStepProps<T, Extract<keyof T, string>>>
    | React.ReactElement<WizardStepProps<T, Extract<keyof T, string>>>[];
}

/**
 * Hook to help with using the Wizard component.
 * Not required, but highly recommended.
 *
 * @export
 * @template T
 * @param {T} initialResult
 * @returns {[T, Dispatch<(input: T) => void>]}
 */
export function useWizard<T extends StringObjectType<T>>(
  initialResult: T
): [T, Dispatch<(input: T) => void>] {
  return React.useReducer<Reducer<T, (input: T) => void>>(
    produce,
    initialResult
  );
}

/**
 * Component for creating workflows. A generic component that enforces strong types.
 *
 * @template T
 * @param {WizardProps<T>} {
 *   result,
 *   updateResult,
 *   steps,
 *   initialStepNumber = 0,
 *   workflowContext,
 *   onConfirmCancel,
 *   children,
 * }
 * @returns {JSX.Element}
 * @example
 * interface SampleResult {
 *   input: number;
 *   review?: undefined;
 * }
 *
 * const [result, setResult] = useWizard<SampleResult>({ input: 0 });
 * return (
 *   <Wizard<SampleResult>
 *     result={result}
 *     updateResult={setResult}
 *     steps={["input", "review"]}
 *   >
 *     <WizardStep<SampleResult, "input">
 *       stepId="input"
 *       stepTitle="Input Step"
 *       preventUnmount
 *       component={InputStep}
 *     ></WizardStep>
 *     <WizardStep<SampleResult, "review">
 *       stepId="review"
 *       stepTitle="Review Step"
 *       component={ReviewStep}
 *     ></WizardStep>
 *   </Wizard>
 * );
 */
function Wizard<T extends StringObjectType<T>, TContext = undefined>({
  result,
  updateResult,
  steps,
  initialStepNumber = 0,
  workflowContext,
  onConfirmCancel,
  children,
}: WizardProps<T, TContext>): JSX.Element {
  const [currentStepNumber, setStepNumber] = useState<number>(
    initialStepNumber
  );
  const [farthestStep, setFarthestStep] = useState<number>(initialStepNumber);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const stepTitleReference = useRef<HTMLSpanElement>(null);
  const maxStepNumber = steps.length - 1;

  const scrollToTitle = useCallback((): void => {
    if (
      stepTitleReference.current &&
      stepTitleReference.current.scrollIntoView
    ) {
      stepTitleReference.current.scrollIntoView();
    }
  }, []);

  // Scroll to top when step changes.
  useEffect(() => {
    scrollToTitle();
  }, [scrollToTitle, currentStepNumber]);

  // Keep farthest step in sync with current step.
  useEffect(() => {
    if (currentStepNumber > farthestStep) {
      setFarthestStep(currentStepNumber);
    }
  }, [currentStepNumber, farthestStep]);

  const onCancelDismiss = (isCanceled: boolean): void => {
    if (isCanceled && onConfirmCancel) {
      onConfirmCancel();
    }

    setShowCancelModal(false);
  };

  const stepArray = React.Children.toArray(children);
  const currentStepId = steps[currentStepNumber];
  const { props: currentStepProps } = stepArray[currentStepNumber] as any;

  const renderedSteps = stepArray.map((step) => {
    const { stepId, preventUnmount } = (step as any).props;

    if (stepId === currentStepId || preventUnmount) {
      const isHidden = stepId !== currentStepId && preventUnmount;

      return (
        <div
          hidden={isHidden}
          className={isHidden ? wizardStyles["wizard-step-hidden"] : ""}
          key={`${stepId}-holder`}
        >
          {React.cloneElement(step as React.ReactElement, { key: stepId })}
        </div>
      );
    }

    return null;
  });

  return (
    <div className={workflowStyles["custom-layout"]}>
      {showCancelModal && (
        <DefaultWizardCancelModal
          visible={showCancelModal}
          onDismiss={onCancelDismiss}
        />
      )}
      <div className={workflowStyles["custom-col-nav"]}>
        <WizardNavigation
          steps={stepArray.map((step) => (step as any).props.stepTitle)}
          active={currentStepNumber}
          farthest={farthestStep}
          chooseStep={setStepNumber}
        />
      </div>
      <div className={workflowStyles["custom-col-content"]}>
        <WizardWorkflowContextProvider<TContext> context={workflowContext}>
          <WizardStepContextProvider
            maxStepNumber={maxStepNumber}
            currentStepNumber={currentStepNumber}
            setStepNumber={setStepNumber}
            onCancel={setShowCancelModal}
          >
            <WizardResultContextProvider
              result={result}
              setResult={updateResult}
            >
              <Form
                header={
                  <span ref={stepTitleReference}>
                    <h1>{currentStepProps.stepTitle}</h1>
                    {currentStepProps.stepDescription && (
                      <h3>{currentStepProps.stepDescription}</h3>
                    )}
                    <div className={workflowStyles["custom-mobile-nav"]}>
                      <p>
                        Step {currentStepNumber + 1} of {steps.length}
                      </p>
                    </div>
                  </span>
                }
              >
                {renderedSteps}
              </Form>
            </WizardResultContextProvider>
          </WizardStepContextProvider>
        </WizardWorkflowContextProvider>
      </div>
    </div>
  );
}

export default Wizard;
